import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";

// Stage 6/de-vendor: local replacements for the two @datasirpi/hooks
// exports this app's local design-system components actually use
// (`useControlledState` in Tabs/FileUpload/AppSidebar/Slider,
// `usePrefersReducedMotion` in Chart) — ported letter-for-letter from the
// vendor's compiled bundle. The rest of that package's exports
// (useDebouncedValue, useDisclosure, useMediaQuery, useClickOutside,
// useId, useEventCallback, useFocusVisible, useIsomorphicLayoutEffect,
// useLoadingDelay, useThrottledValue, ClockProvider) are NOT ported —
// zero real usage anywhere in this app.
export interface ControlledStateOptions<T> {
  value?: T | undefined;
  defaultValue?: T | undefined;
  onChange?: ((next: T) => void) | undefined;
}

export function useControlledState<T>({ value, defaultValue, onChange }: ControlledStateOptions<T>): [T | undefined, (next: T) => void] {
  const isControlled = value !== undefined;
  const [internal, setInternal] = useState(defaultValue);
  const onChangeRef = useRef(onChange);
  // The vendor's own version wrote onChangeRef.current directly during
  // render (a common "always call the latest callback" idiom) — this
  // repo's react-hooks/refs rule forbids writing a ref during render, so
  // it's synced in an effect instead. Functionally equivalent for every
  // real caller here: `set` (which reads the ref) only ever runs from a
  // later user interaction, after React has already flushed this effect.
  useEffect(() => {
    onChangeRef.current = onChange;
  });
  const set = useCallback(
    (next: T) => {
      if (!isControlled) setInternal(next);
      onChangeRef.current?.(next);
    },
    [isControlled]
  );
  return [isControlled ? value : internal, set];
}

// `usePrefersReducedMotion` is "convenience over useMediaQuery" in the
// vendor package; useMediaQuery itself has no other real caller here, so
// its general form isn't ported — just the one query this app needs,
// inlined directly.
export function usePrefersReducedMotion(): boolean {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    // Genuine synchronous read of an external system (the media query's
    // current value) needed on mount to avoid a flash of the wrong
    // (default `false`) state before the "change" listener below can ever
    // fire — same justification as NumberInput.tsx's focus-resync effect.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMatches(mq.matches);
    const onChange = () => setMatches(mq.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return matches;
}

/* Phase 4.4 — "are we on the client yet?", for components that portal.
 *
 * createPortal needs a real DOM node, which does not exist during the server
 * render, so any component that may be open on its very first render (a
 * Popover with defaultOpen, say) has to wait for hydration before portalling.
 *
 * The usual `useState(false)` + `useEffect(() => setMounted(true))` does this
 * too, but it is a setState in an effect — an extra render pass on every
 * mount, and something this repo's lint rules correctly flag. useSyncExternalStore
 * is the sanctioned way to read a value that differs between server and
 * client: it returns the server snapshot during SSR and the client snapshot
 * afterwards, with no effect and no second render.
 *
 * The store never changes, so `subscribe` is a no-op that returns a no-op
 * unsubscribe — the value flips once, at hydration, which React handles.
 */
const NOOP_UNSUBSCRIBE = () => {};
const subscribeNever = () => NOOP_UNSUBSCRIBE;
const getClientSnapshot = () => true;
const getServerSnapshot = () => false;

export function useIsClient(): boolean {
  return useSyncExternalStore(subscribeNever, getClientSnapshot, getServerSnapshot);
}
